import { useEffect, useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { TrendingUp, TrendingDown, Send, MessageSquare, Mail } from "lucide-react";
import CohortDrillDrawer from "@/pages/admin/CohortDrillDrawer";

// ---------- mini chart primitives ----------
function Sparkline({ series, height = 56, stroke = "var(--ff-primary)", fill = "rgba(26,67,49,0.10)" }) {
  if (!series?.length) return null;
  const max = Math.max(1, ...series.map(p => p.amount ?? p.count ?? 0));
  const W = 320, H = height, pad = 4;
  const dx = (W - 2 * pad) / Math.max(1, series.length - 1);
  const pts = series.map((p, i) => {
    const v = p.amount ?? p.count ?? 0;
    const x = pad + i * dx;
    const y = H - pad - (v / max) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const area = `${pad},${H - pad} ${pts} ${(W - pad).toFixed(1)},${H - pad}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <polygon points={area} fill={fill} />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" />
    </svg>
  );
}

function BarChart({ data, labelKey = "week_start", valueKey = "count" }) {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map(d => d[valueKey]));
  return (
    <div className="flex items-end gap-1.5 h-24 mt-3" data-testid="analytics-bar-chart">
      {data.map((d, i) => {
        const h = Math.max(4, (d[valueKey] / max) * 100);
        const label = String(d[labelKey] || "").slice(5); // MM-DD
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${label}: ${d[valueKey]}`}>
            <div className="w-full rounded-t" style={{ height: `${h}%`, background: "var(--ff-primary)", minHeight: 4 }} />
            <span className="text-[10px] text-[var(--ff-text-muted)]">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function CohortHeatmap({ rows, onCellClick }) {
  if (!rows?.length) {
    return <p className="text-sm text-[var(--ff-text-muted)] mt-4">No cohort data yet — needs customer signups + memberships.</p>;
  }
  return (
    <div className="overflow-x-auto mt-4" data-testid="analytics-cohort-grid">
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr className="text-left text-[var(--ff-text-muted)]">
            <th className="py-2 pr-3 font-medium">Cohort</th>
            <th className="py-2 pr-3 font-medium">Size</th>
            {rows[0].retention.map((r, i) => (
              <th key={i} className="py-2 px-2 font-medium text-center">M{i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cohort} className="border-t border-[var(--ff-border)]">
              <td className="py-2 pr-3 font-mono">{r.cohort}</td>
              <td className="py-2 pr-3">{r.size}</td>
              {r.retention.map((c, i) => {
                const alpha = Math.min(1, c.pct / 100);
                return (
                  <td key={i} className="py-2 px-1 text-center" title={`${c.month}: ${c.retained}/${r.size} · click to view members`}>
                    <button
                      type="button"
                      onClick={() => onCellClick?.(r.cohort, i)}
                      className="inline-block px-2 py-1 rounded text-[11px] font-semibold hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ff-primary)] transition-all cursor-pointer"
                      style={{
                        background: `rgba(26,67,49,${alpha * 0.85 + 0.05})`,
                        color: alpha > 0.45 ? "#fff" : "var(--ff-text-primary)",
                      }}
                      data-testid={`cohort-cell-${r.cohort}-m${i}`}
                    >
                      {c.pct}%
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetricCard({ label, value, sub, trend }) {
  return (
    <div className="card-flat p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="overline">{label}</p>
      <p className="font-display text-3xl mt-2">{value}</p>
      {sub && (
        <p className="text-xs text-[var(--ff-text-muted)] mt-1 flex items-center gap-1">
          {trend === "up" && <TrendingUp size={12} className="text-[var(--ff-primary)]" />}
          {trend === "down" && <TrendingDown size={12} className="text-red-700" />}
          {sub}
        </p>
      )}
    </div>
  );
}

// ---------- SMS sandbox panel ----------
function SmsSandboxPanel() {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("Test SMS from The Farm To You admin sandbox.");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/admin/notifications/test-sms/recent?limit=8")
    .then(r => setRecent(r.data.rows || [])).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/notifications/test-sms", { to: to.trim(), message });
      setResult(data);
      loadRecent();
    } catch (e) {
      setResult({ sent: false, reason: e.response?.data?.detail || "Failed" });
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-5 mt-6" data-testid="sms-sandbox-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="overline">SMS sandbox</p>
          <h3 className="font-display text-2xl mt-1 flex items-center gap-2">
            <MessageSquare size={22}/> Send a test SMS
          </h3>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-2 max-w-xl leading-relaxed">
            Test your Twilio pipeline live. Phone must be E.164 (e.g. <code className="font-mono text-xs">+1XXXYYYZZZZ</code>). All sends are logged for audit.
          </p>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-3 gap-3">
        <label className="block">
          <span className="overline">Recipient</span>
          <input
            className="input-base mt-2"
            placeholder="+15555550123"
            value={to}
            onChange={e => setTo(e.target.value)}
            data-testid="sms-sandbox-to"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Message ({message.length}/320)</span>
          <input
            className="input-base mt-2"
            maxLength={320}
            value={message}
            onChange={e => setMessage(e.target.value)}
            data-testid="sms-sandbox-message"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <button
          onClick={send}
          disabled={busy || !to.trim() || !message.trim()}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="sms-sandbox-send-btn"
        >
          <Send size={14}/> {busy ? "Sending…" : "Send test SMS"}
        </button>
        {result && (
          <span className={`text-sm ${result.sent ? "text-emerald-700" : "text-rose-700"}`} data-testid="sms-sandbox-result">
            {result.sent ? "✓ Sent" : `× ${result.reason || "Failed"}`}
          </span>
        )}
      </div>

      {recent.length > 0 && (
        <div className="mt-5">
          <p className="overline">Recent sends</p>
          <ul className="mt-2 text-xs text-[var(--ff-text-muted)] space-y-1.5" data-testid="sms-sandbox-history">
            {recent.slice(0, 8).map((r) => (
              <li key={r.id} className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                <span>·</span>
                <span className="font-mono">{r.to}</span>
                <span>·</span>
                <span className={r.sent ? "text-emerald-700" : "text-rose-700"}>{r.sent ? "✓ sent" : "× " + (r.reason || "fail").slice(0, 80)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- Email sandbox panel ----------
function EmailSandboxPanel() {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Hello from The Farm To You");
  const [body, setBody] = useState("This is a test message from the admin sandbox.");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/admin/notifications/test-email/recent?limit=8")
    .then(r => setRecent(r.data.rows || [])).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/notifications/test-email", { to: to.trim(), subject, body });
      setResult(data);
      loadRecent();
    } catch (e) {
      setResult({ sent: false, reason: e.response?.data?.detail || "Failed" });
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-5 mt-6" data-testid="email-sandbox-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="overline">Email sandbox</p>
          <h3 className="font-display text-2xl mt-1 flex items-center gap-2">
            <Mail size={22}/> Send a test email
          </h3>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-2 max-w-xl leading-relaxed">
            Test your Resend pipeline live. Renders with the same brand template used by autoresponders + digests. All sends are logged for audit.
          </p>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="overline">Recipient email</span>
          <input
            className="input-base mt-2"
            type="email"
            placeholder="you@example.com"
            value={to}
            onChange={e => setTo(e.target.value)}
            data-testid="email-sandbox-to"
          />
        </label>
        <label className="block">
          <span className="overline">Subject</span>
          <input
            className="input-base mt-2"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            data-testid="email-sandbox-subject"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="overline">Body</span>
          <textarea
            rows={4}
            className="input-base mt-2"
            value={body}
            onChange={e => setBody(e.target.value)}
            data-testid="email-sandbox-body"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <button
          onClick={send}
          disabled={busy || !to.trim() || !subject.trim() || !body.trim()}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="email-sandbox-send-btn"
        >
          <Send size={14}/> {busy ? "Sending…" : "Send test email"}
        </button>
        {result && (
          <span className={`text-sm ${result.sent ? "text-emerald-700" : "text-rose-700"}`} data-testid="email-sandbox-result">
            {result.sent ? "✓ Sent" : `× ${result.reason || "Failed"}`}
          </span>
        )}
      </div>

      {recent.length > 0 && (
        <div className="mt-5">
          <p className="overline">Recent sends</p>
          <ul className="mt-2 text-xs text-[var(--ff-text-muted)] space-y-1.5" data-testid="email-sandbox-history">
            {recent.slice(0, 8).map((r) => (
              <li key={r.id} className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                <span>·</span>
                <span className="font-mono truncate max-w-[200px]">{r.to}</span>
                <span>·</span>
                <span className="truncate">{(r.subject || "").slice(0, 40)}</span>
                <span>·</span>
                <span className={r.sent ? "text-emerald-700" : "text-rose-700"}>{r.sent ? "✓ sent" : "× " + (r.reason || "fail").slice(0, 30)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- weekly digest panel ----------
function WeeklyDigestPanel() {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  const loadPreview = () => api.get("/admin/digest/preview").then(r => setPreview(r.data)).catch(() => {});
  const loadHistory = () => api.get("/admin/digest/history").then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadPreview(); loadHistory(); }, []);

  const sendNow = async () => {
    setSending(true); setMsg("");
    try {
      const { data } = await api.post("/admin/digest/send-now");
      const deliv = data.deliveries.map(d => `${d.channel}→${d.to}: ${d.sent ? "✓ sent" : "× " + (d.reason || "fail")}`).join(" · ");
      setMsg(deliv || "No channels configured.");
      loadHistory();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!preview) return null;
  const s = preview.settings;
  const sched = `${s.cron_day_of_week.toUpperCase()} ${String(s.cron_hour).padStart(2, "0")}:${String(s.cron_minute).padStart(2, "0")} ${s.cron_tz}`;

  return (
    <div className="card-flat p-5 mt-6" data-testid="weekly-digest-panel">
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6 justify-between">
        <div className="flex-1 min-w-0">
          <p className="overline">Weekly digest</p>
          <h3 className="font-display text-2xl mt-1">Your business pulse, auto-delivered.</h3>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-2 leading-relaxed">
            Every Monday 8 AM ET, you'll get a text and/or email summarizing the past week. Below is the live preview if it sent right now.
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs text-[var(--ff-text-muted)]">
            <span aria-hidden>📅</span>
            <span data-testid="digest-schedule">{sched}</span>
            <span>·</span>
            <span data-testid="digest-channels">channels: {(s.channels || []).join(", ") || "none"}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={sendNow}
            disabled={sending}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="digest-send-now-btn"
          >
            <Send size={16} /> {sending ? "Sending…" : "Send now"}
          </button>
        </div>
      </div>

      <div className="mt-5 p-4 rounded-lg bg-[var(--ff-muted)] border border-[var(--ff-border)] text-sm font-mono leading-relaxed whitespace-pre-wrap" data-testid="digest-sms-preview">
        {preview.rendered.sms}
      </div>

      {msg && <p className="mt-3 text-xs text-[var(--ff-text-muted)]" data-testid="digest-send-msg">{msg}</p>}

      {history.length > 0 && (
        <div className="mt-5">
          <p className="overline">Recent sends</p>
          <ul className="mt-2 text-xs text-[var(--ff-text-muted)] space-y-1" data-testid="digest-history">
            {history.slice(0, 5).map((h, i) => {
              const sentTo = (h.deliveries || []).map(d => `${d.channel}${d.sent ? '✓' : '×'}`).join(" ");
              return (
                <li key={i}>
                  {(h.computed_at || h._history_id || "").slice(0, 16).replace("T", " ")} · {sentTo || "no channels"}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- main analytics tab ----------
export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [drill, setDrill] = useState(null); // {cohort, offset}

  useEffect(() => {
    api.get("/admin/analytics")
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Failed to load analytics"));
  }, []);

  if (err) return <p className="text-red-700 mt-6" data-testid="analytics-error">{err}</p>;
  if (!data) return <p className="text-[var(--ff-text-muted)] mt-6" data-testid="analytics-loading">Loading analytics…</p>;

  return (
    <div data-testid="admin-analytics">
      <Seo title="Analytics" path="/admin" />

      {/* KPI cards */}
      <div className="grid md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        <MetricCard label="MRR" value={`$${data.mrr.toLocaleString()}`} sub={`ARR · $${data.arr.toLocaleString()}`} />
        <MetricCard label="Active members" value={data.active_members} sub={`+${data.new_members_30d} in last 30 days`} trend={data.new_members_30d > 0 ? "up" : undefined} />
        <MetricCard label="Churn (30d)" value={`${data.churn_rate_pct}%`} sub="of base at start of period" trend={data.churn_rate_pct > 5 ? "down" : "up"} />
        <MetricCard label="GMV (30d)" value={`$${data.gmv_30d.toLocaleString()}`} sub={`Platform · $${data.platform_revenue_30d.toLocaleString()}`} />
      </div>

      {/* Trends row */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="card-flat p-5" data-testid="analytics-gmv-trend">
          <p className="overline">GMV · last 30 days</p>
          <div className="mt-3"><Sparkline series={data.gmv_trend} height={88} /></div>
          <p className="text-xs text-[var(--ff-text-muted)] mt-2">
            Peak day: {data.gmv_trend.reduce((a, b) => b.amount > a.amount ? b : a, { amount: 0 }).date || "—"}
          </p>
        </div>
        <div className="card-flat p-5" data-testid="analytics-signups">
          <p className="overline">Customer signups · last 8 weeks</p>
          <BarChart data={data.signups_by_week} labelKey="week_start" valueKey="count" />
        </div>
      </div>

      {/* Top vendors */}
      <div className="card-flat p-5 mt-6" data-testid="analytics-top-vendors">
        <p className="overline">Top vendors · last 90 days</p>
        {data.top_vendors.length === 0 ? (
          <p className="text-sm text-[var(--ff-text-muted)] mt-3">No vendor orders in the last 90 days yet.</p>
        ) : (
          <table className="w-full mt-3 text-sm">
            <thead>
              <tr className="text-left text-[var(--ff-text-muted)] border-b border-[var(--ff-border)]">
                <th className="py-2 font-medium">#</th>
                <th className="py-2 font-medium">Farm</th>
                <th className="py-2 font-medium">Location</th>
                <th className="py-2 font-medium text-right">Units</th>
                <th className="py-2 font-medium text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.top_vendors.map((v, i) => (
                <tr key={v.vendor_id} className="border-b border-[var(--ff-border)] last:border-0">
                  <td className="py-3 font-mono text-[var(--ff-text-muted)]">{i + 1}</td>
                  <td className="py-3 font-medium">{v.farm_name}</td>
                  <td className="py-3 text-[var(--ff-text-secondary)]">{v.location}</td>
                  <td className="py-3 text-right">{v.units_sold}</td>
                  <td className="py-3 text-right font-semibold">${v.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cohort heatmap */}
      <div className="card-flat p-5 mt-6" data-testid="analytics-cohort">
        <p className="overline">Cohort retention · member-month active</p>
        <p className="text-xs text-[var(--ff-text-muted)] mt-1">% of each signup cohort still on an active membership N months later. <span className="italic">Click a cell to see the members.</span></p>
        <CohortHeatmap rows={data.cohort_retention} onCellClick={(cohort, offset) => setDrill({ cohort, offset })} />
      </div>

      <WeeklyDigestPanel />
      <SmsSandboxPanel />
      <EmailSandboxPanel />

      {drill && (
        <CohortDrillDrawer
          cohort={drill.cohort}
          offset={drill.offset}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}
